import { useCallback, useEffect, useRef, useState } from "react";
import { isObject, isUndefined, omitBy } from "lodash";

interface UsePaginationProps<T, S extends object> {
  // Constrain S to extend object
  action: (filter: S) => Promise<void>;
  remoteData: T[];
  defaultPage?: number;
  defaultLimit?: number;
  initialFilter?: S;
}

/**
 * Custom hook for implementing pagination in a React component.
 *
 * @param {Object} options - Options object.
 * @param {Function} options.action - The action function that fetches the data for the current page.
 * @param {Array|Object} options.remoteData - The data returned by the action function.
 * @param {number} [options.defaultPage=1] - The default page number.
 * @param {number} [options.defaultLimit=10] - The default number of items per page.
 * @param {Object} [options.initialFilter={}] - The initial filter object.
 * @param {*} options.id - The identifier for the data to be fetched.
 * @returns {Object} - An object containing pagination-related values and functions.
 */
export const usePagination = <T, S extends object>({
  action,
  remoteData,
  defaultPage = 1,
  defaultLimit = 10,
  initialFilter = {} as S,
}: UsePaginationProps<T, S>) => {
  // State variables for the current page number, number of items per page, and data
  const [page, _setPage] = useState(defaultPage);
  const [limit, _setLimit] = useState(defaultLimit);
  const limitRef = useRef(limit); // create a ref to hold the current value of limit
  const [data, setData] = useState<T[] | null>(null);
  const [loading, setLoading] = useState(false);

  // State variable for the filter object
  const [filter, setFilter] = useState<S>({
    ...initialFilter,
  });

  /**
   * Requests data for the specified ID and filter object using the action function.
   *
   * @param {string} id - The ID of the application to request.
   * @param {object} filterObj - An object containing filter parameters to apply to the request.
   */
  const request = useCallback(
    async (filterObj: S) => {
      setLoading(true);
      await action(omitBy(filterObj, isUndefined) as S);
      setLoading(false);
    },
    [action],
  );

  /**
   * Sets the limit state variable and updates the data for the current page.
   *
   * @param {number} limit - The number of items per page.
   * @returns {void}
   */
  const setLimit = (limit: number) => {
    _setLimit(limit);
    limitRef.current = limit;
    setPage(1, limit);
  };

  /**
   * Sets the page state variable and updates the data for the new page.
   *
   * @param {number} page - The page number.
   * @param {number} [replaceLimit] - An optional number to use as the limit for the current page.
   * @returns {void}
   */
  const setPage = useCallback(
    (page: number, replaceLimit?: number) => {
      const limitToUse = replaceLimit || limit;

      _setPage(page);
      request({
        ...filter,
        page,
        limit: limitToUse,
      }).then();
    },
    [request, limit, filter],
  );

  /**
   * Updates the specified key-value pair or multiple key-value pairs in the filter object.
   *
   * @param {(string|object)} field - The key or an object of key-value pairs to be updated.
   * @param {*} [value] - The value to be set for the key if "name" is a string.
   */
  const onChange = (field: string | Partial<S>, value?: S) => {
    const newFilter = {
      ...filter,
      ...(isObject(field) ? field : { [field]: value }),
      page: 1,
      limit,
    };

    setFilter(newFilter);
    _setPage(1);
    request(newFilter).then();
  };

  const initialFilterRef = useRef(initialFilter);

  // Run the request function on mount
  useEffect(() => {
    setFilter(initialFilterRef.current);
    _setPage(1);

    request({
      ...initialFilterRef.current,
      page: 1,
      limit: limitRef.current,
    }).then();
  }, [request]);

  // Sets the data state variable to the value of the remoteData argument when it changes
  useEffect(() => {
    if (remoteData) {
      setData(remoteData);
    }
  }, [remoteData]);

  // reset filter
  const replaceFilter = (objectFilter: S) => {
    setFilter({ ...objectFilter, page: 1, limit });
    _setPage(1);
  };

  // Returns pagination-related values and functions
  return {
    page,
    limit,
    setPage,
    setLimit,
    onChange,
    filter,
    data,
    loading,
    replaceFilter,
  };
};
